import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  PlusCircle,
  Zap,
  Calendar,
  History,
  QrCode,
  BarChart3,
  Building2,
  Settings,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppStore } from '@/stores/app.store';

interface NavItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
}

interface NavSection {
  title?: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    items: [
      { label: 'Dashboard', icon: LayoutDashboard, path: '/' },
    ],
  },
  {
    title: 'Deals',
    items: [
      { label: 'Create Deal', icon: PlusCircle, path: '/deals/create' },
      { label: 'Active Deals', icon: Zap, path: '/deals/active' },
      { label: 'Scheduled', icon: Calendar, path: '/deals/scheduled' },
      { label: 'History', icon: History, path: '/deals/history' },
    ],
  },
  {
    title: 'Operations',
    items: [
      { label: 'Scanner Setup', icon: QrCode, path: '/scanner/setup' },
      { label: 'Analytics', icon: BarChart3, path: '/analytics' },
    ],
  },
  {
    title: 'Settings',
    items: [
      { label: 'Store Profile', icon: Building2, path: '/store' },
      { label: 'Settings', icon: Settings, path: '/settings' },
    ],
  },
];

export function Sidebar() {
  const location = useLocation();
  const collapsed = useAppStore((s) => s.sidebarCollapsed);

  return (
    <aside
      className={cn(
        'flex flex-col border-r bg-dashboard-sidebar text-white transition-all',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      <div className="flex h-16 items-center justify-center border-b border-dashboard-sidebarHover px-6">
        <h1 className={cn('text-xl font-bold', collapsed && 'hidden')}>Run2Sale</h1>
        {collapsed && <span className="text-xl font-bold">R2S</span>}
      </div>

      <nav className="flex-1 overflow-y-auto p-4">
        {navSections.map((section, sIndex) => (
          <div key={sIndex} className={cn(sIndex > 0 && 'mt-4')}>
            {section.title && !collapsed && (
              <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
                {section.title}
              </p>
            )}
            {collapsed && sIndex > 0 && (
              <div className="mx-3 my-2 border-t border-dashboard-sidebarHover" />
            )}
            <div className="space-y-1">
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive =
                  item.path === '/'
                    ? location.pathname === '/'
                    : location.pathname.startsWith(item.path);

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 transition-colors',
                      isActive
                        ? 'bg-dashboard-sidebarActive text-white'
                        : 'text-gray-300 hover:bg-dashboard-sidebarHover hover:text-white'
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    {!collapsed && <span>{item.label}</span>}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  );
}
